import {Injectable, isDevMode} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay} from 'rxjs/operators';
import {BankDetails, FicaDetails, KycStatus, VerificationStatus, WebBusinessService} from 'web-angular-client';

import {EcommerceVerificationState} from '../interface/ecommerce-verification-state';

/**
 * Loads an e-commerce merchant's KYC/AVS verification state from their FICA and banking details
 * (used to render the verification cards on the ecommerce landing page), and centralises the
 * verification-status-driven decisions for post-login routing and account settings visibility.
 */
@Injectable({providedIn: 'root'})
export class EcommerceVerificationService
{
    private inFlightState$: Observable<EcommerceVerificationState> | null = null;

    constructor(private webBusinessService: WebBusinessService)
    {
    }

    /**
     * Loads the merchant's current verification state from their FICA and banking details. The
     * FICA and banking calls fail independently — a failure on one is captured as a load-failure
     * flag (so the relevant card can show an error) rather than failing the whole load. Concurrent
     * callers (e.g. the landing page and the sidebar loading together) share a single in-flight
     * request; the cache is released on completion so a later call re-fetches fresh state.
     */
    getVerificationState(): Observable<EcommerceVerificationState>
    {
        if (this.inFlightState$)
        {
            return this.inFlightState$;
        }

        const kycState$ = this.webBusinessService.getFicaDetails().pipe(
            map((ficaDetails: FicaDetails) => ({kycStatus: ficaDetails?.kycStatus, kycLoadFailed: false})),
            catchError((error) =>
            {
                this.debugLog('Failed to load FICA details for verification state', error);
                return of({kycStatus: undefined, kycLoadFailed: true});
            })
        );

        const avsState$ = this.webBusinessService.getBankingDetails().pipe(
            map((bankDetails: BankDetails) =>
                ({avsVerificationStatus: bankDetails?.verificationStatus, avsLoadFailed: false})),
            catchError((error) =>
            {
                this.debugLog('Failed to load banking details for verification state', error);
                return of({avsVerificationStatus: undefined, avsLoadFailed: true});
            })
        );

        this.inFlightState$ = forkJoin([kycState$, avsState$]).pipe(
            map(([kyc, avs]) => ({
                kycStatus: kyc.kycStatus,
                avsVerificationStatus: avs.avsVerificationStatus,
                kycLoadFailed: kyc.kycLoadFailed,
                avsLoadFailed: avs.avsLoadFailed
            })),
            finalize(() => this.inFlightState$ = null),
            shareReplay({bufferSize: 1, refCount: false})
        );
        return this.inFlightState$;
    }

    /**
     * Logs a message only in development; verification load failures are surfaced to the user
     * through the on-page error cards, so the production console stays clean.
     *
     * @param message the message to log
     * @param error the error to log alongside the message
     */
    private debugLog(message: string, error: unknown): void
    {
        if (isDevMode())
        {
            console.log(message, error);
        }
    }

    /**
     * Whether the merchant should see the onboarding landing page instead of the dashboard. Verified
     * KYC goes to the dashboard; a submitted-but-pending KYC stays on the landing page only while the
     * merchant is still a guest admin; everything else (failed, or never started) lands so they can
     * start, fix or resubmit. AVS status is not considered — a failed AVS surfaces through the
     * landing page's AVS card and the account settings visibility instead of changing the destination.
     *
     * @param verificationState the merchant's current verification state
     * @param isGuestAdmin whether the merchant still holds the GUEST_ADMIN role
     */
    shouldDisplayLandingPage(verificationState: EcommerceVerificationState, isGuestAdmin: boolean): boolean
    {
        if (verificationState.kycStatus === KycStatus.VERIFIED)
        {
            return false;
        }
        if (verificationState.kycStatus === KycStatus.PENDING)
        {
            return isGuestAdmin;
        }
        return true;
    }

    /**
     * Whether the account settings menu item should be hidden — only while a guest admin's KYC is
     * unresolved (neither verified nor failed) and their AVS has not failed. A verified or failed
     * KYC, or a failed AVS, keeps it visible so the merchant can manage or resubmit their details.
     *
     * @param verificationState the merchant's current verification state
     * @param isGuestAdmin whether the merchant still holds the GUEST_ADMIN role
     */
    isAccountSettingsHidden(verificationState: EcommerceVerificationState, isGuestAdmin: boolean): boolean
    {
        return isGuestAdmin
            && verificationState.kycStatus !== KycStatus.VERIFIED
            && verificationState.kycStatus !== KycStatus.FAILED
            && verificationState.avsVerificationStatus !== VerificationStatus.FAILED;
    }
}
